import dayjs from 'dayjs';
import EventEmitter from 'eventemitter3';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import { useAuthUserStore } from '@lib/store';
import { CoreUtils, LocalStorageUtils } from '.';
import { AuthApi, type JwtToken } from '@shared/api/rest';
import type { AuthUser } from '@shared/types';

const isDevelopment = process.env.NODE_ENV === 'development';

export class AuthUtils {
  static readonly isAuthEventName = 'isAuth';
  static readonly eventIsAuth = new EventEmitter<string, boolean>();
  static authDataCached: AuthUser | undefined = undefined;
  private static latestCallRefreshAuthDate = dayjs();
  private static readonly accessTokenMinutes = 30; //It's constant value for current project
  private static readonly publicPages = [];

  static init = async () => {
    this.authDataCached = LocalStorageUtils.getIppUser();
    this.runLogOutTimer();
    this.runRefreshAuthTimer();
    AuthUtils.updateRefreshAuthDate();
    //Update token when use mousemove
    window.addEventListener('mousemove', this.onCallbackDocumentMouseMove);
    window.addEventListener('keypress', this.onCallbackDocumentMouseMove);
  };

  static isAuth = () => this.authDataCached != null && this.isValidExpiresDate();

  static redirectToLoginPage = () => {
    const currentUrl = window.location.href.toLowerCase();
    if (this.publicPages.some((page) => currentUrl.includes(page))) return;
    window.location.href = process.env.LOGIN_URL!;
  };

  static updateAuthData = (authData: AuthUser) => {
    const dateNow = dayjs();
    authData.expiresDate = dateNow.add(this.accessTokenMinutes, 'minutes').unix();
    const { data } = this.parseJwtTokenData(authData);
    LocalStorageUtils.setIppUser(data);
    useAuthUserStore.setState({ data: data });
    this.authDataCached = data;
    this.runLogOutTimer();
    if (!this.timerRefreshAuth) this.runRefreshAuthTimer();
    this.eventIsAuth.emit(this.isAuthEventName, true);
  };

  static parseJwtTokenData = (authData: AuthUser) => {
    const data = structuredClone(authData || this.authDataCached);

    const decodedToken: JwtToken = jwtDecode(data.accessToken);
    data.permissions = decodedToken.client_Permission?.reduce((acc: Record<string, boolean>, perm) => ({ ...acc, [perm]: true }), {}) || {};
    const roles = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    if (typeof roles === 'string') {
      data.roles = roles.split(',').reduce((acc: Record<string, boolean>, role) => ({ ...acc, [role]: true }), {});
    } else if (Array.isArray(roles)) {
      data.roles = roles.reduce((acc: Record<string, boolean>, role) => ({ ...acc, [role]: true }), {});
    }

    return { data, decodedToken };
  };

  static getTempTokenForLogIn = () => new URLSearchParams(window.location.search).get('userId');

  static logOut = () => {
    if (isDevelopment) return;

    this.disableRefreshAuthTimer();
    this.disableLogOutTimer();
    const authData = Object.freeze(this.authDataCached);
    Cookies.remove('authUser');
    Cookies.remove('refreshToken');
    LocalStorageUtils.removeIppUser();
    LocalStorageUtils.removePartners();
    LocalStorageUtils.removeSelectedPartnerId();
    this.authDataCached = undefined;
    // this.eventIsAuth.emit(this.isAuthEventName, false);
    this.redirectToLogOutPage(authData!);
  };

  private static redirectToLogOutPage = (authData: AuthUser) => {
    const data = this.parseJwtTokenData(authData);
    if (!data?.decodedToken) return this.redirectToLoginPage();
    const logOutUrl = data.decodedToken.client_Logout;
    if (!logOutUrl) return this.redirectToLoginPage();
    window.location.href = logOutUrl;
  };

  private static updateRefreshAuthDate = () => {
    if (!this.isAuth()) return;
    this.latestCallRefreshAuthDate = dayjs();
  };

  private static refreshAuth = async () => {
    try {
      const authData = this.authDataCached;
      if (!authData) return;
      if (!this.isAuth()) return;

      if (!isDevelopment) {
        const now = dayjs();
        const expDate = this.latestCallRefreshAuthDate.add(70, 'seconds');
        if (expDate.valueOf() < now.valueOf()) return;
      }

      const result = await AuthApi.refreshToken(authData.refreshToken);
      this.updateAuthData(result as unknown as AuthUser);
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
  };

  private static isValidExpiresDate = () => {
    if (!this.authDataCached) return false;
    const nowDate = dayjs();
    const expDate = dayjs.unix(this.authDataCached.expiresDate);
    const isValid = expDate > nowDate;
    return isValid;
  };

  // private static parseAccessToken = (authData?: AuthUser) => {
  //   const data = authData || this.authDataCached;
  //   if (data == null) return undefined;
  //   const result = JSON.parse(atob(data.accessToken.split('.')[1]));
  //   return result;
  // };

  private static readonly timeRefreshInterval = 60 * 1000; //1 min
  private static timerRefreshAuth: NodeJS.Timeout | null = null;
  private static runRefreshAuthTimer = () => {
    if (!this.isAuth()) return;

    this.disableRefreshAuthTimer();
    this.timerRefreshAuth = setInterval(async () => await this.refreshAuth(), this.timeRefreshInterval);
  };
  private static disableRefreshAuthTimer = () => {
    if (!this.timerRefreshAuth) return;
    clearInterval(this.timerRefreshAuth);
    this.timerRefreshAuth = null;
  };

  private static timerLogOut: NodeJS.Timeout | null = null;

  private static runLogOutTimer = () => {
    const authData = this.authDataCached;
    if (!authData) return;

    if (!this.isValidExpiresDate()) {
      this.logOut();
      return;
    }

    const expDate = dayjs.unix(authData.expiresDate);
    const timeLeft = expDate.valueOf() - dayjs().valueOf();

    this.disableLogOutTimer();
    this.timerLogOut = setTimeout(() => this.logOut(), timeLeft);
  };

  private static disableLogOutTimer = () => {
    if (this.timerLogOut) clearTimeout(this.timerLogOut);
  };

  private static onCallbackDocumentMouseMove = CoreUtils.debounce(() => AuthUtils.updateRefreshAuthDate(), 250);
}
